import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Rating,
  Avatar,
  Tooltip,
  Badge,
  Stack,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifiedIcon,
  Person as PersonIcon,
  ShoppingBag as ProductIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { formatReviewDate } from '../../../utils/dateUtils';
import { ecommerceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface Review {
  id: string;
  productId: string;
  productName: string;
  memberId: string;
  memberName: string;
  orderId: string | null;
  rating: number;
  title: string | null;
  comment: string;
  images: string | null;
  isApproved: boolean;
  isVerifiedPurchase: boolean;
  rejectionReason: string | null;
  showName: boolean;
  helpfulCount: number;
  unhelpfulCount: number;
  adminReply: string | null;
  adminReplyAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
}

interface ReviewsManagementProps {
  companyId: string | null;
}

interface Stats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  averageRating: number;
}

const ReviewsManagement: React.FC<ReviewsManagementProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<number>(0);
  const [productFilter, setProductFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [verifiedOnlyFilter, setVerifiedOnlyFilter] = useState<boolean>(false);
  
  // Detail Modal
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Approve Modal
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [reviewToApprove, setReviewToApprove] = useState<Review | null>(null);
  
  // Reject Modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewToReject, setReviewToReject] = useState<Review | null>(null);
  
  // Stats
  const [stats, setStats] = useState<Stats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    averageRating: 0,
  });

  // Yorumları yükle
  const fetchReviews = async () => {
    if (!companyId) {
      setError(t('ecommerceReviews.errors.companyIdNotFound'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 [REVIEWS MANAGEMENT] Yorumlar yükleniyor...');
      
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (ratingFilter > 0) {
        params.append('rating', ratingFilter.toString());
      }
      if (productFilter) {
        params.append('productId', productFilter);
      }
      if (verifiedOnlyFilter) {
        params.append('verifiedOnly', 'true');
      }
      
      const url = `/api/ecommerce/reviews/all${params.toString() ? '?' + params.toString() : ''}`;
      console.log('🌐 [REVIEWS MANAGEMENT] API URL:', url);
      
      const response = await ecommerceApi.get(url);
      const data = response.data;
      console.log('📦 [REVIEWS MANAGEMENT] API yanıtı:', data);

      if (data.success) {
        setReviews(data.data || []);
        calculateStats(data.data || []);
        console.log('✅ [REVIEWS MANAGEMENT] Yorumlar yüklendi:', data.data?.length || 0);
      } else {
        setError(data.message || t('ecommerceReviews.errors.loadReviewsFailed'));
      }
    } catch (err: any) {
      console.error('❌ [REVIEWS MANAGEMENT] Hata:', err);
      setError(err.message || t('ecommerceReviews.errors.loadReviewsFailed'));
    } finally {
      setLoading(false);
    }
  };

  // İstatistikleri hesapla
  const calculateStats = (reviewsData: Review[]) => {
    const total = reviewsData.length;
    const pending = reviewsData.filter(r => !r.isApproved && !r.rejectionReason).length;
    const approved = reviewsData.filter(r => r.isApproved).length;
    const rejected = reviewsData.filter(r => r.rejectionReason).length;
    const averageRating = total > 0 
      ? reviewsData.reduce((sum, r) => sum + r.rating, 0) / total 
      : 0;

    setStats({
      total,
      pending,
      approved,
      rejected,
      averageRating: Math.round(averageRating * 10) / 10,
    });
  };

  // İlk yükleme
  useEffect(() => {
    fetchReviews();
  }, [companyId, statusFilter, ratingFilter, productFilter, verifiedOnlyFilter]);

  // Onaylama modalını aç
  const openApproveModal = (review: Review) => {
    setReviewToApprove(review);
    setApproveModalOpen(true);
  };

  // Yorumu onayla
  const handleApprove = async () => {
    if (!companyId || !reviewToApprove) return;

    try {
      console.log('✅ [APPROVE REVIEW] Yorum onaylanıyor:', reviewToApprove.id);
      
      const { data } = await ecommerceApi.post('/api/ecommerce/reviews/approve', {
        reviewId: reviewToApprove.id,
        adminReply: null
      });

      if (data.success) {
        setSuccess(t('ecommerceReviews.messages.reviewApproved'));
        setApproveModalOpen(false);
        setReviewToApprove(null);
        fetchReviews(); // Listeyi yenile
      } else {
        setError(data.message || t('ecommerceReviews.errors.approveFailed'));
      }
    } catch (err: any) {
      console.error('❌ [APPROVE REVIEW] Hata:', err);
      setError(err.message || t('ecommerceReviews.errors.approveFailed'));
    }
  };

  // Reddetme modalını aç
  const openRejectModal = (review: Review) => {
    setReviewToReject(review);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  // Yorumu reddet
  const handleReject = async () => {
    if (!companyId || !reviewToReject || !rejectReason.trim()) {
      setError(t('ecommerceReviews.errors.rejectionReasonRequired'));
      return;
    }

    try {
      console.log('❌ [REJECT REVIEW] Yorum reddediliyor:', reviewToReject.id);
      
      const { data } = await ecommerceApi.post('/api/ecommerce/reviews/reject', {
        reviewId: reviewToReject.id,
        rejectionReason: rejectReason,
      });

      if (data.success) {
        setSuccess(t('ecommerceReviews.messages.reviewRejected'));
        setRejectModalOpen(false);
        setReviewToReject(null);
        setRejectReason('');
        fetchReviews(); // Listeyi yenile
      } else {
        setError(data.message || t('ecommerceReviews.errors.rejectFailed'));
      }
    } catch (err: any) {
      console.error('❌ [REJECT REVIEW] Hata:', err);
      setError(err.message || t('ecommerceReviews.errors.rejectFailed'));
    }
  };

  // Detay modalını aç
  const openDetailModal = (review: Review) => {
    setSelectedReview(review);
    setDetailModalOpen(true);
  };

  // Pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtrelenmiş ve sayfalanmış yorumlar
  const getFilteredReviews = () => {
    let filtered = [...reviews];

    // Arama filtresi
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        r =>
          r.comment.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.memberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.productName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredReviews = getFilteredReviews();
  const paginatedReviews = filteredReviews.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Status badge rengi
  const getStatusColor = (review: Review) => {
    if (review.rejectionReason) return 'error';
    if (review.isApproved) return 'success';
    return 'warning';
  };

  // Status text
  const getStatusText = (review: Review) => {
    if (review.rejectionReason) return t('ecommerceReviews.status.rejected');
    if (review.isApproved) return t('ecommerceReviews.status.approved');
    return t('ecommerceReviews.status.pendingApproval');
  };

  // Tarih formatla
  const formatDate = (dateString: string) => {
    return formatReviewDate(dateString);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        📝 {t('ecommerceReviews.title')}
      </Typography>

      {/* İstatistik Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {t('ecommerceReviews.stats.totalReviews')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {t('ecommerceReviews.stats.pending')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {t('ecommerceReviews.stats.approved')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {stats.approved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {t('ecommerceReviews.stats.rejected')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                {stats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {t('ecommerceReviews.stats.averageRating')}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stats.averageRating.toFixed(1)}
                </Typography>
                <StarIcon sx={{ color: 'warning.main', fontSize: 28 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtreler */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('ecommerceReviews.filters.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceReviews.table.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('ecommerceReviews.table.status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('ecommerceReviews.filters.all')}</MenuItem>
                <MenuItem value="pending">{t('ecommerceReviews.status.pending')}</MenuItem>
                <MenuItem value="approved">{t('ecommerceReviews.status.approved')}</MenuItem>
                <MenuItem value="rejected">{t('ecommerceReviews.status.rejected')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceReviews.table.rating')}</InputLabel>
              <Select
                value={ratingFilter}
                label={t('ecommerceReviews.table.rating')}
                onChange={(e) => setRatingFilter(Number(e.target.value))}
              >
                <MenuItem value={0}>{t('ecommerceReviews.filters.allRatings')}</MenuItem>
                <MenuItem value={5}>{t('ecommerceReviews.filters.stars', { count: 5 })}</MenuItem>
                <MenuItem value={4}>{t('ecommerceReviews.filters.stars', { count: 4 })}</MenuItem>
                <MenuItem value={3}>{t('ecommerceReviews.filters.stars', { count: 3 })}</MenuItem>
                <MenuItem value={2}>{t('ecommerceReviews.filters.stars', { count: 2 })}</MenuItem>
                <MenuItem value={1}>{t('ecommerceReviews.filters.star', { count: 1 })}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceReviews.filters.verifiedPurchase')}</InputLabel>
              <Select
                value={verifiedOnlyFilter ? 'yes' : 'no'}
                label={t('ecommerceReviews.filters.verifiedPurchase')}
                onChange={(e) => setVerifiedOnlyFilter(e.target.value === 'yes')}
              >
                <MenuItem value="no">{t('ecommerceReviews.filters.all')}</MenuItem>
                <MenuItem value="yes">{t('ecommerceReviews.filters.verifiedOnly')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchReviews}
                disabled={loading}
              >
                {t('ecommerceReviews.actions.refresh')}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setStatusFilter('all');
                  setRatingFilter(0);
                  setProductFilter('');
                  setSearchQuery('');
                  setVerifiedOnlyFilter(false);
                }}
              >
                {t('ecommerceReviews.actions.clearFilters')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Hata/Başarı Mesajları */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Yorumlar Tablosu */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.product')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.user')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.rating')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.review')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('ecommerceReviews.table.date')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('ecommerceReviews.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedReviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {t('ecommerceReviews.empty.noReviews')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReviews.map((review) => (
                  <TableRow key={review.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ProductIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ maxWidth: 150 }} noWrap>
                          {review.productName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {review.isVerifiedPurchase && (
                          <Tooltip title={t('ecommerceReviews.filters.verifiedPurchase')}>
                            <VerifiedIcon fontSize="small" color="success" />
                          </Tooltip>
                        )}
                        <Typography variant="body2">
                          {review.showName ? review.memberName : t('ecommerceReviews.table.anonymousUser')}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Rating value={review.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        {review.title && (
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {review.title.length > 30 ? `${review.title.substring(0, 30)}...` : review.title}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {review.comment.length > 50 ? `${review.comment.substring(0, 50)}...` : review.comment}
                        </Typography>
                        {review.images && (
                          <Chip
                            icon={<ImageIcon />}
                            label={t('ecommerceReviews.table.hasImage')}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(review)}
                        color={getStatusColor(review)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(review.createdAt)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t('ecommerceReviews.actions.details')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openDetailModal(review)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!review.isApproved && !review.rejectionReason && (
                          <>
                            <Tooltip title={t('ecommerceReviews.actions.approve')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openApproveModal(review)}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('ecommerceReviews.actions.reject')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openRejectModal(review)}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredReviews.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('ecommerceReviews.pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>

      {/* Detay Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('ecommerceReviews.detail.title')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReview && (
            <Stack spacing={2}>
              {/* Ürün Bilgisi */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceReviews.table.product')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ProductIcon color="action" />
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {selectedReview.productName}
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Kullanıcı Bilgisi */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceReviews.table.user')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon color="action" />
                  <Typography variant="body1">
                    {selectedReview.showName ? selectedReview.memberName : t('ecommerceReviews.table.anonymousUser')}
                  </Typography>
                  {selectedReview.isVerifiedPurchase && (
                    <Chip
                      icon={<VerifiedIcon />}
                      label={t('ecommerceReviews.filters.verifiedPurchase')}
                      size="small"
                      color="success"
                    />
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Puan */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Puan
                </Typography>
                <Rating value={selectedReview.rating} readOnly />
              </Box>

              <Divider />

              {/* Başlık */}
              {selectedReview.title && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ecommerceReviews.detail.reviewTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedReview.title}
                    </Typography>
                  </Box>
                  <Divider />
                </>
              )}

              {/* Yorum */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceReviews.table.review')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedReview.comment}
                </Typography>
              </Box>

              {/* Resimler */}
              {selectedReview.images && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ecommerceReviews.detail.images')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {JSON.parse(selectedReview.images).map((img: string, index: number) => (
                        <Avatar
                          key={index}
                          src={img}
                          variant="rounded"
                          sx={{ width: 100, height: 100 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

              <Divider />

              {/* Durum */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceReviews.table.status')}
                </Typography>
                <Chip
                  label={getStatusText(selectedReview)}
                  color={getStatusColor(selectedReview)}
                />
                {selectedReview.rejectionReason && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>{t('ecommerceReviews.detail.rejectionReason')}:</strong> {selectedReview.rejectionReason}
                    </Typography>
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Tarih Bilgileri */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('ecommerceReviews.detail.dateInformation')}
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>{t('ecommerceReviews.detail.created')}:</strong> {formatDate(selectedReview.createdAt)}
                  </Typography>
                  {selectedReview.approvedAt && (
                    <Typography variant="body2">
                      <strong>{t('ecommerceReviews.detail.approved')}:</strong> {formatDate(selectedReview.approvedAt)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedReview && !selectedReview.isApproved && !selectedReview.rejectionReason && (
            <>
              <Button
                startIcon={<ApproveIcon />}
                color="success"
                variant="contained"
                onClick={() => {
                  setDetailModalOpen(false);
                  openApproveModal(selectedReview);
                }}
              >
                {t('ecommerceReviews.actions.approve')}
              </Button>
              <Button
                startIcon={<RejectIcon />}
                color="error"
                variant="outlined"
                onClick={() => {
                  setDetailModalOpen(false);
                  openRejectModal(selectedReview);
                }}
              >
                {t('ecommerceReviews.actions.reject')}
              </Button>
            </>
          )}
          <Button onClick={() => setDetailModalOpen(false)}>{t('ecommerceReviews.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Onaylama Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceReviews.approveModal.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('ecommerceReviews.approveModal.info')}
          </Alert>
          {reviewToApprove && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('ecommerceReviews.table.product')}:</strong> {reviewToApprove.productName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('ecommerceReviews.table.user')}:</strong> {reviewToApprove.showName ? reviewToApprove.memberName : t('ecommerceReviews.table.anonymousUser')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('ecommerceReviews.table.rating')}:</strong> {reviewToApprove.rating} / 5
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{t('ecommerceReviews.table.review')}:</strong>
              </Typography>
              <Typography variant="body2" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, mt: 0.5 }}>
                {reviewToApprove.comment}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 'bold' }}>
            {t('ecommerceReviews.approveModal.confirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveModalOpen(false)}>{t('ecommerceReviews.actions.cancel')}</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={handleApprove}
          >
            {t('ecommerceReviews.approveModal.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reddetme Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceReviews.rejectModal.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceReviews.rejectModal.description')}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={t('ecommerceReviews.rejectModal.placeholder')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            error={!rejectReason.trim()}
            helperText={!rejectReason.trim() ? t('ecommerceReviews.errors.rejectionReasonRequired') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectModalOpen(false)}>{t('ecommerceReviews.actions.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectReason.trim()}
          >
            {t('ecommerceReviews.actions.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewsManagement;

